import { Stack, Tooltip } from '@mui/material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsNew } from '@retail/calculus/types';
import { AddButton, CalculusActionPortal } from '@retail/components';
import { useFetchPriceSuggestionTypesWithCount } from '@retail/price-suggestion/data-access';
import {
  FollowPriceSuggestionSchema,
  PriceSuggestionConditionsTable,
} from '@retail/price-suggestion/components';
import { useFormContext } from 'react-hook-form';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useDisclosure } from '@shared/hooks';
import { useMemo, useState } from 'react';
import { useLanguageSelector } from '@retail/app/hooks';
import { PriceSuggestionConditionActions } from './PriceSuggestionConditionActions';
import { getAvailableTypes } from '@retail/price-suggestion/utils';

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  supplierNumber?: number;
  assortmentId: number;
}

export function PriceSuggestionConditions({
  conditions,
  isFetching,
  supplierNumber,
  assortmentId,
}: Props) {
  const { language } = useLanguageSelector();
  const t = useCalculusTFunction();
  const { appTenant, orgUnitType } = useSelectedOrgUnit();

  const { userCanCreate, userCanUpdate, userCanRead } = usePermissionsForCurrentUser();
  const userCanCreatePriceSuggestionConditions = userCanCreate(
    'PRICE_CALCULATION__PRICE_SUGGESTIONS'
  );
  const userCanUpdatePriceSuggestionsConditions = userCanUpdate(
    'PRICE_CALCULATION__PRICE_SUGGESTIONS'
  );
  const localPriceSuggestionIsEnabled = useIsFeatureEnabled('local-price-suggestions');

  const userCanReadLocalPriceSuggestions =
    userCanRead('PRICE_SUGGESTIONS') && localPriceSuggestionIsEnabled;

  const { isOpen: createCondition, onToggle: _toggleCreateDialog } = useDisclosure();
  const [editCondition, setEdit] = useState<ConditionsNew | undefined>();
  const [deactivateConditionId, setDeactivate] = useState<number | undefined>();
  const [deleteConditionId, setDelete] = useState<number | undefined>();

  const conditionActions = userCanUpdatePriceSuggestionsConditions
    ? { setEdit, setDeactivate, setDelete }
    : undefined;

  const toggleCreateDialog = userCanCreatePriceSuggestionConditions
    ? _toggleCreateDialog
    : undefined;

  const { watch } = useFormContext<FollowPriceSuggestionSchema>();

  const { data: priceSuggestionTypesWithCount = [], isFetching: isFetchingPriceSuggestionsTypes } =
    useFetchPriceSuggestionTypesWithCount(
      {
        assortmentId,
        mgSupplierId: supplierNumber,
        productGroup: (watch('productGroup') ?? watch('mainGroup') ?? watch('overGroup'))
          ?.groupNumber,
        mgItemNumber: watch('item')?.mgItemNumber,
      },
      { suspense: false }
    );

  const availablePriceSuggestions = useMemo(() => {
    const availableTypes = getAvailableTypes({
      countryCode: appTenant.countryCode,
      orgUnitType,
      canReadLocal: userCanReadLocalPriceSuggestions,
      assortmentLevel: !supplierNumber,
    });
    return priceSuggestionTypesWithCount.filter((type) =>
      availableTypes.includes(type.suggestionType)
    );
  }, [
    priceSuggestionTypesWithCount,
    appTenant.countryCode,
    orgUnitType,
    userCanReadLocalPriceSuggestions,
    supplierNumber,
  ]);

  return (
    <Stack flexGrow={1} gap={2} overflow="auto">
      <CalculusActionPortal>
        {userCanCreatePriceSuggestionConditions &&
          (availablePriceSuggestions.length === 0 ? (
            <Tooltip title={t('calculus.noPriceSuggestion')}>
              <div>
                <AddButton disabled onClick={toggleCreateDialog}>
                  {t('calculus.followPriceSuggestionCondition')}
                </AddButton>
              </div>
            </Tooltip>
          ) : (
            <AddButton onClick={toggleCreateDialog}>
              {t('calculus.followPriceSuggestionCondition')}
            </AddButton>
          ))}
      </CalculusActionPortal>

      <PriceSuggestionConditionsTable
        loading={isFetchingPriceSuggestionsTypes || isFetching}
        priceSuggestionConditions={conditions}
        noPriceSuggestionTypes={availablePriceSuggestions.length === 0}
        language={language}
        supplierLevel={!!supplierNumber}
        conditionActions={conditionActions}
        openCreateDialog={toggleCreateDialog}
      />
      <PriceSuggestionConditionActions
        supplierNumber={supplierNumber}
        priceSuggestionTypes={availablePriceSuggestions}
        createCondition={createCondition}
        editCondition={editCondition}
        deactivateConditionId={deactivateConditionId}
        deleteConditionId={deleteConditionId}
        closeCreate={toggleCreateDialog}
        closeEdit={() => setEdit(undefined)}
        closeDeactivate={() => setDeactivate(undefined)}
        closeDelete={() => setDelete(undefined)}
      />
    </Stack>
  );
}
